/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type { DirectoryUser, FlagOverrideRow } from "~/features/feature-flags/shared/contracts"
import type { DirectoryCompany, FlagPanel } from "~/features/feature-flags/client/model/flagPanel"
import { matchesSearch } from "~/features/feature-flags/client/model/filters"
import {
  FEATURE_FLAGS,
  FLAG_KEYS_BY_CATEGORY,
  type FeatureFlagKey,
  type FlagDefinition,
} from "~/features/feature-flags/shared/registry"
import {
  companyStatus,
  userStatus,
  type CompanyStatus,
  type FlagState,
} from "~/features/feature-flags/client/model/status"

export const COMPANY_FILTERS = ["all", "overrides", "on", "off"] as const
export type CompanyFilter = (typeof COMPANY_FILTERS)[number]

export const COMPANY_FILTER_LABELS: Record<CompanyFilter, string> = {
  all: "All",
  overrides: "Overrides",
  on: "On",
  off: "Off",
}

/** One company × flag: its setting, status and the user overrides of its members. */
export type CompanyFlagRow = {
  flagKey: FeatureFlagKey
  orgId: string
  orgName: string
  /** The company override, null when it follows the default. */
  override: boolean | null
  /** What "Default" means for this company. */
  defaultValue: boolean
  status: CompanyStatus
  /** Non-staff members with a user override. */
  userOverrides: number
}

export function companyFlagRow(
  panel: FlagPanel,
  flagKey: FeatureFlagKey,
  orgId: string,
): CompanyFlagRow {
  const { snapshot } = panel
  return {
    flagKey,
    orgId,
    orgName: panel.companyName(orgId),
    override: snapshot.override({ flagKey, scope: "org", targetId: orgId }),
    defaultValue: snapshot.defaultValue(flagKey),
    status: companyStatus(panel, flagKey, orgId),
    userOverrides: panel
      .userOverrides(flagKey)
      .filter((o) => !panel.isStaffUser(o.targetId) && panel.userOrgIds(o.targetId).includes(orgId))
      .length,
  }
}

export type FlagCompanyRow = DirectoryCompany &
  CompanyFlagRow & {
    /** A custom flag's own company. Listed first. */
    builtFor: boolean
  }

/** Every company for one flag. A custom flag's own companies come first. */
export function flagCompanyRows(panel: FlagPanel, flagKey: FeatureFlagKey): FlagCompanyRow[] {
  const ownCompanies = new Set(panel.builtFor(flagKey))
  return panel.companies
    .map((company) => ({
      ...company,
      ...companyFlagRow(panel, flagKey, company.id),
      builtFor: ownCompanies.has(company.id),
    }))
    .toSorted((a, b) => Number(b.builtFor) - Number(a.builtFor) || a.name.localeCompare(b.name))
}

export type OrganizationFlagRow = CompanyFlagRow & { definition: FlagDefinition }

/** Every flag for one company, in category order. */
export const organizationFlagRows = (panel: FlagPanel, orgId: string): OrganizationFlagRow[] =>
  FLAG_KEYS_BY_CATEGORY.map((key) => ({
    definition: FEATURE_FLAGS[key],
    ...companyFlagRow(panel, key, orgId),
  }))

const matchesCompanyFilter = (row: FlagCompanyRow, filter: CompanyFilter) => {
  if (filter === "all") return true
  if (filter === "overrides") return row.override !== null
  return row.status.state === filter
}

export const filterCompanyRows = (
  rows: readonly FlagCompanyRow[],
  filter: CompanyFilter,
  search: string,
) =>
  rows.filter(
    (row) => matchesCompanyFilter(row, filter) && matchesSearch(search, row.name, row.slug, row.id),
  )

export type FlagUserRow = {
  id: string
  /** `null` when the user isn't in the directory. */
  user: DirectoryUser | null
  /** Company names, or "Hakio staff". */
  companies: string
  value: boolean
  /** What the user gets across their companies, prerequisites included. */
  state: FlagState
  /** The prerequisite keeping the user off, when that is why. */
  blockedBy: string | null
  note: string | null
  updatedBy: string
  updatedAt: Date
}

const userCompanies = (
  user: DirectoryUser | null | undefined,
  companyName: (orgId: string) => string,
) => {
  if (!user) return ""
  if (user.isStaff) return "Hakio staff"
  return user.orgIds.map(companyName).join(", ")
}

/** Every user override for one flag, by name with unknown users last. */
export function flagUserRows(panel: FlagPanel, flagKey: FeatureFlagKey): FlagUserRow[] {
  return panel
    .userOverrides(flagKey)
    .map((override) => {
      const user = panel.user(override.targetId) ?? null
      const resolved = userStatus(panel.snapshot, flagKey, override.targetId, user?.orgIds ?? [])
      return {
        id: override.targetId,
        user,
        companies: userCompanies(user, panel.companyName),
        value: override.value,
        state: resolved.state,
        blockedBy: resolved.blockedBy,
        note: override.note,
        updatedBy: override.updatedBy,
        updatedAt: override.updatedAt,
      }
    })
    .toSorted(
      (a, b) =>
        Number(!a.user) - Number(!b.user) ||
        (a.user?.name ?? a.id).localeCompare(b.user?.name ?? b.id),
    )
}

export const filterUserRows = (rows: readonly FlagUserRow[], search: string) =>
  rows.filter((row) =>
    matchesSearch(search, row.user?.name, row.user?.email, row.companies, row.id),
  )

export type NamedOverride = FlagOverrideRow & { name: string }

const byName = (a: NamedOverride, b: NamedOverride) => a.name.localeCompare(b.name)

/** A flag's company and user override counts (Hakio staff apart), and the overrides named:
 * companies then non-staff users, and Hakio staff's alone. */
export function overrideSummary(panel: FlagPanel, flagKey: FeatureFlagKey) {
  const companies = panel
    .companyOverrides(flagKey)
    .map((o) => ({ ...o, name: panel.companyName(o.targetId) }))
    .toSorted(byName)
  const users = panel
    .userOverrides(flagKey)
    .map((o) => ({ ...o, name: panel.userName(o.targetId) }))
    .toSorted(byName)
  const staff = users.filter((o) => panel.isStaffUser(o.targetId))
  const customers = users.filter((o) => !panel.isStaffUser(o.targetId))
  return {
    companyCount: companies.length,
    userCount: customers.length,
    staff,
    listed: [...companies, ...customers],
  }
}
